import { useEffect, useRef, useState } from 'react';
import { getWhatsAppQR, getWhatsAppEstado, desconectarWhatsApp, enviarWhatsApp } from '../services/api';
import Modal from './ui/Modal';

interface Destinatario {
  id: number;
  contratante: string;
  telefono: string;
  numeroPoliza: string;
  producto: string;
  fechaVencimiento: string;
  fechaLimitePago: string;
}

interface WhatsAppModalProps {
  destinatarios: Destinatario[];
  onClose: () => void;
}

const STORAGE_KEY = 'wa_mensaje_template';
const DEFAULT_TEMPLATE =
  'Hola {contratante}, te informamos que tu póliza *{numeroPoliza}* ({producto}) tiene un recibo pendiente. Fecha de vencimiento: {fechaVencimiento}. Fecha límite de pago: {fechaLimitePago}. Para cualquier aclaración comunícate con tu agente de seguros. Portal: https://crm.verdeantequeradigital.com/';

function buildMensaje(template: string, d: Destinatario): string {
  return template
    .replace(/{contratante}/g, d.contratante)
    .replace(/{numeroPoliza}/g, d.numeroPoliza)
    .replace(/{producto}/g, d.producto)
    .replace(/{fechaVencimiento}/g, d.fechaVencimiento || '—')
    .replace(/{fechaLimitePago}/g, d.fechaLimitePago || '—');
}

type WaState = 'checking' | 'disconnected' | 'waiting_qr' | 'connected';

export default function WhatsAppModal({ destinatarios, onClose }: WhatsAppModalProps) {
  const [waState, setWaState] = useState<WaState>('checking');
  const [phone, setPhone] = useState<string>('');
  const [qr, setQr] = useState<string>('');
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState<{ sent: number; failed: number } | null>(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [template, setTemplate] = useState<string>(() => {
    return localStorage.getItem(STORAGE_KEY) ?? DEFAULT_TEMPLATE;
  });
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPoll = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const checkEstado = async () => {
    try {
      const res = await getWhatsAppEstado();
      if (res.data.connected) {
        setWaState('connected');
        setPhone(res.data.phone ?? '');
        stopPoll();
      } else {
        setWaState('disconnected');
      }
    } catch {
      setWaState('disconnected');
    }
  };

  useEffect(() => {
    checkEstado();
    return () => stopPoll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConectar = async () => {
    setWaState('waiting_qr');
    setQr('');
    setErrorMsg('');

    pollRef.current = setInterval(async () => {
      try {
        const res = await getWhatsAppQR();
        if (res.data.connected) {
          stopPoll();
          setWaState('connected');
          setPhone(res.data.phone ?? '');
        } else if (res.data.qr) {
          setQr(res.data.qr);
        }
      } catch {
        stopPoll();
        setWaState('disconnected');
      }
    }, 3000);
  };

  const handleDesconectar = async () => {
    stopPoll();
    try { await desconectarWhatsApp(); } catch { /* ignore */ }
    setWaState('disconnected');
    setQr('');
    setPhone('');
  };

  const handleTemplateChange = (val: string) => {
    setTemplate(val);
    localStorage.setItem(STORAGE_KEY, val);
  };

  const handleEnviar = async () => {
    setSending(true);
    setErrorMsg('');
    setProgress(null);
    try {
      const mensajes = destinatarios
        .filter(d => d.telefono)
        .map(d => ({ telefono: d.telefono, texto: buildMensaje(template, d) }));

      const res = await enviarWhatsApp(mensajes);
      setProgress({ sent: res.data.sent ?? mensajes.length, failed: res.data.failed ?? 0 });
    } catch (err: any) {
      setErrorMsg(err.response?.data?.message || 'Error al enviar mensajes');
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal title="WhatsApp" onClose={onClose}>
      <div className="space-y-4">
        {/* Estado chip */}
        <div className="flex items-center gap-3">
          <span className={`inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full font-medium ${
            waState === 'connected' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
          }`}>
            <span className={`w-1.5 h-1.5 rounded-full ${waState === 'connected' ? 'bg-green-500' : 'bg-red-500'}`} />
            {waState === 'connected' ? `Conectado${phone ? ` (${phone})` : ''}` :
             waState === 'waiting_qr' ? 'Esperando QR...' :
             waState === 'checking' ? 'Verificando...' : 'Desconectado'}
          </span>
          {waState === 'connected' && (
            <button onClick={handleDesconectar}
              className="text-xs text-gray-500 hover:text-red-600 underline">
              Desconectar
            </button>
          )}
          {(waState === 'disconnected') && (
            <button onClick={handleConectar}
              className="text-xs bg-green-600 text-white px-3 py-1 rounded-lg hover:bg-green-700">
              Conectar WhatsApp
            </button>
          )}
        </div>

        {/* QR code */}
        {waState === 'waiting_qr' && (
          <div className="flex flex-col items-center gap-2 py-4">
            {qr ? (
              <>
                <p className="text-sm text-gray-600">Escanea este código QR con tu WhatsApp</p>
                <img src={qr} alt="WhatsApp QR" className="w-56 h-56 border rounded-lg" />
              </>
            ) : (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <div className="w-4 h-4 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
                Generando QR...
              </div>
            )}
          </div>
        )}

        {/* Destinatarios */}
        {waState === 'connected' && !progress && (
          <>
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">
                Destinatarios ({destinatarios.length})
              </p>
              <div className="max-h-36 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                {destinatarios.map(d => (
                  <div key={d.id} className="flex items-center justify-between px-3 py-2 text-sm">
                    <span className="font-medium text-gray-800 truncate">{d.contratante}</span>
                    <span className="text-gray-500 ml-2 flex-shrink-0">{d.telefono || '—'}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm font-medium text-gray-700">Mensaje de cobranza</p>
                <button
                  onClick={() => handleTemplateChange(DEFAULT_TEMPLATE)}
                  className="text-xs text-gray-400 hover:text-gray-600 underline"
                >
                  Restaurar default
                </button>
              </div>
              <textarea
                value={template}
                onChange={e => handleTemplateChange(e.target.value)}
                rows={5}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-xs text-gray-700 leading-relaxed resize-none focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
              <p className="text-xs text-gray-400 mt-1">
                Usa: <code className="bg-gray-100 px-1 rounded">{'{contratante}'}</code>{' '}
                <code className="bg-gray-100 px-1 rounded">{'{numeroPoliza}'}</code>{' '}
                <code className="bg-gray-100 px-1 rounded">{'{producto}'}</code>{' '}
                <code className="bg-gray-100 px-1 rounded">{'{fechaVencimiento}'}</code>{' '}
                <code className="bg-gray-100 px-1 rounded">{'{fechaLimitePago}'}</code>
              </p>
            </div>

            {errorMsg && <p className="text-red-500 text-sm">{errorMsg}</p>}

            <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
              <button onClick={onClose}
                className="px-4 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                Cancelar
              </button>
              <button
                onClick={handleEnviar}
                disabled={sending || destinatarios.filter(d => d.telefono).length === 0}
                className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-60 flex items-center gap-2"
              >
                {sending && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                {sending ? 'Enviando...' : `Enviar ${destinatarios.filter(d => d.telefono).length} mensajes`}
              </button>
            </div>
          </>
        )}

        {/* Resultado */}
        {progress && (
          <div className="text-center py-6 space-y-3">
            <div className="text-4xl">✅</div>
            <p className="text-lg font-semibold text-gray-800">Mensajes enviados</p>
            <p className="text-sm text-gray-600">
              Enviados: <span className="text-green-600 font-medium">{progress.sent}</span> &nbsp;·&nbsp;
              Fallidos: <span className="text-red-500 font-medium">{progress.failed}</span>
            </p>
            <button onClick={onClose}
              className="mt-2 px-5 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700">
              Cerrar
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}
